/**
 * Utilities for turning arbitrary request / response payloads into a flat,
 * Datadog-safe set of log attributes. Used by withRequestLogging (server) and
 * fetchWithTrace (client) so every HTTP call in the system emits filterable
 * structured fields — language, target_role, interview_company, status, etc.
 */

import { isPersonalDataKey, isSensitiveKey, redactSecretsInString } from "./log-redaction"

export const REDACTED_PERSONAL_DATA = "<redacted:pii>"

const MAX_STRING_LEN = 500
const MAX_ATTRS = 60

function isSensitive(key: string): boolean {
  return isSensitiveKey(key)
}

function truncate(value: string, maxLen: number): string {
  if (value.length <= maxLen) return value
  return `${value.slice(0, maxLen)}…(+${value.length - maxLen})`
}

/**
 * Flattens a JSON-like value into { `${prefix}_field`: scalar } pairs safe
 * to send to Datadog. Rules:
 *   - Scalars (string/number/boolean) are included as-is; strings are
 *     truncated to MAX_STRING_LEN and a `_length` sibling is emitted.
 *   - Arrays emit `_count` and, when elements are objects with a `role`
 *     field, a comma-joined `_roles` summary (useful for chat messages).
 *   - Nested objects are summarised as `_type: "object"` with top-level
 *     scalar children flattened under dot-prefixed names.
 *   - Keys matching sensitive patterns are dropped entirely.
 *   - Output is capped at MAX_ATTRS entries to keep logs bounded.
 */
export function flattenForLog(
  input: unknown,
  prefix: string,
  opts: { maxStringLen?: number } = {}
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (input == null || typeof input !== "object" || Array.isArray(input)) {
    if (Array.isArray(input)) {
      out[`${prefix}_count`] = input.length
    }
    return out
  }
  const maxLen = opts.maxStringLen ?? MAX_STRING_LEN
  let count = 0
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (count >= MAX_ATTRS) break
    if (isSensitive(key)) continue
    const field = prefix ? `${prefix}_${key}` : key

    if (value == null) continue

    if (typeof value === "string") {
      // Personal data keeps its length (so the field stays debuggable) but never
      // its content — this is what stopped resume text and interview transcripts
      // from being shipped to Datadog in full.
      if (isPersonalDataKey(key)) {
        out[field] = REDACTED_PERSONAL_DATA
        out[`${field}_length`] = value.length
        count++
        continue
      }
      // Value-level redaction first (catches secrets embedded in otherwise
      // innocent-named fields, e.g. websocketUrl=…?jwt=<key>), then truncate.
      out[field] = truncate(redactSecretsInString(value), maxLen)
      out[`${field}_length`] = value.length
      count++
      continue
    }

    if (typeof value === "number" || typeof value === "boolean") {
      out[field] = value
      count++
      continue
    }

    if (Array.isArray(value)) {
      out[`${field}_count`] = value.length
      if (value.length > 0 && typeof value[0] === "object" && value[0] !== null) {
        const first = value[0] as Record<string, unknown>
        if ("role" in first) {
          out[`${field}_roles`] = value
            .map((v) => (v && typeof v === "object" ? (v as { role?: string }).role : undefined))
            .filter(Boolean)
            .join(",")
        }
      }
      count++
      continue
    }

    if (typeof value === "object") {
      out[`${field}_type`] = "object"
      const nested = flattenForLog(value, field, opts)
      Object.assign(out, nested)
      count++
      continue
    }
  }
  return out
}

/** Attempts to peek at a `fetch` body init value as JSON. Returns {} on anything non-JSON. */
export function attributesFromFetchBody(
  body: BodyInit | null | undefined,
  prefix: string
): Record<string, unknown> {
  if (body == null) return {}
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body)
      return flattenForLog(parsed, prefix)
    } catch {
      return { [`${prefix}_body_type`]: "string", [`${prefix}_body_length`]: body.length }
    }
  }
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
    const obj: Record<string, string> = {}
    body.forEach((v, k) => {
      obj[k] = v
    })
    return flattenForLog(obj, prefix)
  }
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return { [`${prefix}_body_type`]: "FormData" }
  }
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return { [`${prefix}_body_type`]: "Blob", [`${prefix}_body_size`]: body.size }
  }
  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) {
    return { [`${prefix}_body_type`]: "ArrayBuffer", [`${prefix}_body_size`]: body.byteLength }
  }
  return { [`${prefix}_body_type`]: "unknown" }
}

/** True for responses whose body should NOT be read (would break the stream). */
export function isStreamingContentType(contentType: string | null | undefined): boolean {
  if (!contentType) return false
  const ct = contentType.toLowerCase()
  return (
    ct.includes("text/event-stream") ||
    ct.includes("application/octet-stream") ||
    ct.includes("multipart/") ||
    ct.includes("video/") ||
    ct.includes("audio/")
  )
}
